import React, { useEffect } from 'react';
import FormField from './FormField';
import { formatZipCode } from '../utils/formatting';
import { fetchAddressByCEP } from '../utils/paymentApi';
import { FormErrors } from '../types/payment';

interface AddressFormProps {
  formData: any;
  errors: FormErrors;
  onInputChange: (field: string, value: string) => void;
  showComplementField: boolean;
}

const states = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

const AddressForm: React.FC<AddressFormProps> = ({
  formData,
  errors,
  onInputChange,
  showComplementField
}) => {
  const handleZipCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatZipCode(e.target.value);
    onInputChange('customer-zipcode', formatted);

    if (formatted.replace(/\D/g, '').length === 8) {
      const addressData = await fetchAddressByCEP(formatted);
      if (addressData) {
        onInputChange('customer-address', addressData.logradouro);
        onInputChange('customer-neighborhood', addressData.bairro);
        onInputChange('customer-city', addressData.localidade);
        onInputChange('customer-state', addressData.uf);
      }
    }
  };

  return (
    <div className="customer-data-grid">
      <FormField
        label="CEP"
        required
        error={errors['customer-zipcode']}
      >
        <input
          type="text"
          id="customer-zipcode"
          value={formData['customer-zipcode'] || ''}
          onChange={handleZipCodeChange}
          placeholder="00000-000"
          className={errors['customer-zipcode'] ? 'invalid' : ''}
        />
      </FormField>

      <FormField
        label="Estado"
        required
        error={errors['customer-state']}
      >
        <select
          id="customer-state"
          value={formData['customer-state'] || ''}
          onChange={(e) => onInputChange('customer-state', e.target.value)}
          className={errors['customer-state'] ? 'invalid' : ''}
        >
          <option value="">Selecione</option>
          {states.map(state => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        label="Cidade"
        required
        error={errors['customer-city']}
      >
        <input
          type="text"
          id="customer-city"
          value={formData['customer-city'] || ''}
          onChange={(e) => onInputChange('customer-city', e.target.value)}
          placeholder="Sua cidade"
          className={errors['customer-city'] ? 'invalid' : ''}
        />
      </FormField>

      <FormField
        label="Bairro"
        required
        error={errors['customer-neighborhood']}
      >
        <input
          type="text"
          id="customer-neighborhood"
          value={formData['customer-neighborhood'] || ''}
          onChange={(e) => onInputChange('customer-neighborhood', e.target.value)}
          placeholder="Seu bairro"
          className={errors['customer-neighborhood'] ? 'invalid' : ''}
        />
      </FormField>

      <FormField
        label="Endereço"
        required
        error={errors['customer-address']}
      >
        <input
          type="text"
          id="customer-address"
          value={formData['customer-address'] || ''}
          onChange={(e) => onInputChange('customer-address', e.target.value)}
          placeholder="Rua, Avenida, etc."
          className={errors['customer-address'] ? 'invalid' : ''}
        />
      </FormField>

      <FormField
        label="Número"
        required
        error={errors['customer-number']}
      >
        <input
          type="text"
          id="customer-number"
          value={formData['customer-number'] || ''}
          onChange={(e) => onInputChange('customer-number', e.target.value)}
          placeholder="Número"
          className={errors['customer-number'] ? 'invalid' : ''}
        />
      </FormField>

      {showComplementField && (
        <FormField
          label="Complemento"
          error={errors['customer-complement']}
          className="field-optional visible"
        >
          <input
            type="text"
            id="customer-complement"
            value={formData['customer-complement'] || ''}
            onChange={(e) => onInputChange('customer-complement', e.target.value)}
            placeholder="Apto, Bloco, etc. (opcional)"
            className={errors['customer-complement'] ? 'invalid' : ''}
          />
        </FormField>
      )}
    </div>
  );
};

export default AddressForm;